import React, { useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import SearchFilters from 'components/SearchFilters';

import { Popover, PopoverContent, PopoverToggler } from 'lib/popover';
import { placement } from 'lib/popover/types';
import searchClient, {
  FacetsList,
  SearchFilterProps,
  SearchOptions,
} from 'lib/search';

interface SearchFiltersPopoverProps extends SearchFilterProps {
  /**
   * Allows using a custom toggler content
   */
  renderTogglerContent?: () => React.JSX.Element;
  /**
   * Allows using a custom class name for toggler button
   */
  togglerBtnClassName?: string;
  /**
   * Allows setting a custom style for toggler button
   */
  togglerBtnStyle?: React.CSSProperties;
  /**
   * Allows setting a custom width for popover content
   */
  width?: number;
  /**
   * Allows setting a custom max-width for popover content
   */
  maxWidth?: number;
  /**
   * Change default placement for popover content
   */
  placement?: placement;
  /**
   * Flag to control if a badge with the number of selected filters
   * should be shown on top of popover toggler button. Enabled by default.
   */
  showBadgeCount?: boolean;
  /**
   * Custom badge count to be shown on top of popover toggler button.
   */
  badgeCount?: number;
}

export default function SearchFiltersPopover(props: SearchFiltersPopoverProps) {
  const [facets, setFacets] = useState<FacetsList>();
  const [isClientInitialized, setIsClientInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const client = useMemo(
    () => searchClient(props),
    [props.algoliaAppId, props.algoliaApiKey, props.algoliaIndexName]
  );

  useEffect(() => {
    if (!isClientInitialized) {
      client.validateIndex().then(() => setIsClientInitialized(true));
    }
  }, []);

  useEffect(() => {
    async function loadFacets() {
      const searchOptions = {
        readerCompaniesFilter: props.readerCompanyId,
        driversOnly: props.driversOnly,
        includeDeleted: props.includeDeleted,
        searchFilters: props.updateFiltersOnSelect ? props.selectedFilters : [],
      };
      setIsLoading(true);
      const facets = await client.getAllFacets(
        searchOptions,
        props.facetsQuery
      );
      setFacets(facets);
      setIsLoading(false);
    }
    if (isClientInitialized) {
      loadFacets();
    }
  }, [isClientInitialized, props.facetsQuery]);

  // Update available facets when selectedFilters are updated
  // and updateFiltersOnSelect option is active
  useEffect(() => {
    async function loadFacets() {
      const searchOptions: SearchOptions<any> = {
        readerCompaniesFilter: props.readerCompanyId,
        driversOnly: props.driversOnly,
        includeDeleted: props.includeDeleted,
        searchFilters: props.selectedFilters,
      };
      setIsLoading(true);
      const facets = await client.getAllFacets(
        searchOptions,
        props.facetsQuery
      );
      setFacets(facets);
      setIsLoading(false);
    }
    if (isClientInitialized && props.updateFiltersOnSelect) {
      loadFacets();
    }
  }, [
    isClientInitialized,
    props.selectedFilters?.length,
    props.updateFiltersOnSelect,
  ]);

  const togglerDefaultClassnames = clsx(
    'search-filters-button relative inline-block rounded-md border border-gray-200 bg-white p-2 py-3',
    'text-sm leading-[0rem] font-medium uppercase hover:bg-zinc-50 focus:bg-zinc-50 active:bg-zinc-200'
  );
  return (
    <Popover
      className="search-filters-popover isd-search-tw"
      contentContainerClassName="filters-popover-content"
    >
      <PopoverToggler
        className={
          !!props.togglerBtnClassName
            ? props.togglerBtnClassName
            : !!props.togglerBtnStyle
            ? ''
            : togglerDefaultClassnames
        }
        style={props.togglerBtnStyle}
        type="button"
      >
        {props.renderTogglerContent ? (
          props.renderTogglerContent()
        ) : (
          <DefaultTogglerContent />
        )}
        {!!props.selectedFilters?.length && props.showBadgeCount !== false && (
          <FiltersCountBadge
            count={props.badgeCount || props.selectedFilters.length}
          />
        )}
      </PopoverToggler>

      <PopoverContent
        placement={props.placement || 'bottom-end'}
        className="filters-popover-content max-w-[1000px] border border-solid border-gray-200 bg-white p-4"
        style={{
          width: !!props.width ? `${props.width}px` : '500px',
          maxWidth: !!props.maxWidth ? `${props.maxWidth}px` : '1000px',
          zIndex: 1071,
        }}
      >
        {props.children}
        <SearchFilters
          facets={facets}
          client={client}
          isClientInitialized={isClientInitialized}
          isLoading={isLoading}
          {...props}
        />
      </PopoverContent>
    </Popover>
  );
}

const FiltersCountBadge = ({ count }: { count: number }) => (
  // Source: flowbite.com/docs/components/badge/#notification-badge
  <div className="filters-count-badge absolute -end-2 -top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-sky-400 text-sm text-white">
    {count}
  </div>
);

const DefaultTogglerContent = () => (
  // Heroicons bars-3-center-left
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="search-filters-icon h-6 w-6"
    // set pointerEvents as 'none' so it doesn't block the popover from
    // opening when clicking this icon
    pointerEvents="none"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 6.75h16.5M3.75 12H12m-8.25 5.25h16.5"
    />
  </svg>
);
